import { Badge, Button, Card, CardHeader, Drawer, EmptyState, formatDate, useToast } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  DOCUMENT_KINDS,
  DOCUMENT_LABEL,
  IFSC_RE,
  PAN_RE,
  deleteDocument,
  digitsOf,
  documentLink,
  fileProblem,
  formatBytes,
  getDetails,
  listDocuments,
  revealAccount,
  saveDetails,
  uploadDocument,
  type DetailsPatch,
  type DocumentKind,
  type StaffDetails,
  type StaffDocument,
} from "../../api/staff-documents";
import type { StaffMember } from "../../api/staff";
import { useServices } from "../../app/services";
import { today as localDate } from "../../lib/dates";
import { Confirm, Failure, Field, FormError, messageOf, RowsSkeleton, styles as c, useScope } from "../classes/common";
import d from "./staffDocuments.module.css";

/**
 * Staff documents (founder: "do staff profile come with ID details, PAN upload, Bank Account details and so on?").
 * Gates follow the server: staff:view sees masked details and the document list, staff:edit changes them, and
 * revealing the full account number or downloading a document is owner-only today (levers staff.bank_view_full,
 * staff.documents_download). The UI hides what the role cannot do; the server enforces and audits.
 */
export function StaffDocumentsSection({ staff }: { staff: StaffMember }) {
  const { ctx } = useScope();
  // Both cards read routes gated on `staff.documents_view`; without it they are not offered at all.
  if (!ctx.has("staff.documents_view")) return null;
  return (
    <>
      <DetailsCard staff={staff} />
      <DocumentsCard staff={staff} />
    </>
  );
}

const dash = "—";
/** "21 August 1993": a birth date needs its year and no weekday. Pinned to UTC so no zone shifts it. */
export const formatDob = (ymd: string, locale: string) => {
  const [y, m, day] = ymd.split("-").map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(Date.UTC(y, m - 1, day));
};
const aadhaarMasked = (last4: string | null) => (last4 ? `XXXX XXXX ${last4}` : dash);

function DetailsCard({ staff }: { staff: StaffMember }) {
  const { client } = useServices();
  const sc = useScope();
  const [editing, setEditing] = useState(false);
  const q = useQuery({ queryKey: ["staff", ...sc.key, "details", staff.id], queryFn: () => getDetails(client, staff.id), retry: false });
  const canEdit = sc.ctx.has("staff.documents_edit");
  const edit = canEdit ? <Button onClick={() => setEditing(true)}>{q.data?.details ? "Edit details" : "Add details"}</Button> : null;
  return (
    <Card aria-label="ID and bank details">
      <CardHeader title="ID and bank details" subtitle="Aadhaar keeps only the last 4 digits. Bank account numbers are stored encrypted." action={q.data ? edit : null} />
      {q.isPending ? (
        <RowsSkeleton rows={4} />
      ) : q.error ? (
        <div className={c.pad}><Failure error={q.error} title="Could not load ID and bank details" onRetry={() => void q.refetch()} /></div>
      ) : !q.data.details ? (
        <EmptyState compact icon="lock" title="No ID or bank details yet" description={canEdit ? "Add PAN, Aadhaar (last 4 only), date of birth, address, an emergency contact and bank details." : "Someone with staff edit access can add them."} action={edit ?? undefined} />
      ) : (
        <DetailsView staff={staff} details={q.data.details} />
      )}
      {canEdit && q.data ? <DetailsDrawer open={editing} onClose={() => setEditing(false)} staff={staff} details={q.data.details} /> : null}
    </Card>
  );
}

function DetailsView({ staff, details }: { staff: StaffMember; details: StaffDetails }) {
  const { client } = useServices();
  const sc = useScope();
  const [full, setFull] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reveal = useMutation({ mutationFn: () => revealAccount(client, staff.id), onSuccess: (r) => { setError(null); setFull(r.accountNumber); }, onError: (e) => setError(messageOf(e)) });
  // A revealed number does not outlive a minute on screen or a change of record, and is dropped from the mutation cache too.
  const hide = () => { setFull(null); reveal.reset(); };
  useEffect(() => { hide(); }, [staff.id, details.version]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!full) return;
    const t = setTimeout(hide, 60_000);
    return () => clearTimeout(t);
  }, [full]); // eslint-disable-line react-hooks/exhaustive-deps
  const e = details.emergencyContact;
  const b = details.bank;
  const rows: Array<[string, ReactNode]> = [
    ["PAN", details.pan ?? dash],
    ["Aadhaar", aadhaarMasked(details.aadhaarLast4)],
    ["Date of birth", details.dob ? formatDob(details.dob, sc.ctx.locale) : dash],
    ["Address", details.address ?? dash],
    ["Emergency contact", e ? `${e.name}${e.relation ? ` (${e.relation})` : ""} · ${e.phone ?? ""}` : dash],
    ["Account holder", b?.holderName ?? dash],
    [
      "Account number",
      b ? (
        <span className={c.row}>
          <span className={d.mono} aria-label={full ? "Full account number" : "Masked account number"}>{full ?? b.accountMasked ?? dash}</span>
          {sc.ctx.has("staff.bank_view_full") && b.accountMasked ? (
            full ? <Button variant="plain" onClick={hide}>Hide</Button> : <Button variant="plain" busy={reveal.isPending} onClick={() => reveal.mutate()}>Show full number</Button>
          ) : null}
        </span>
      ) : dash,
    ],
    ["IFSC", b?.ifsc ?? dash],
    ["Bank", b?.bankName ?? dash],
  ];
  return (
    <div className={c.pad}>
      <FormError message={error} />
      <dl className={d.dl}>
        {rows.map(([k, v]) => (
          <div key={k} className={d.kv}><dt>{k}</dt><dd>{v}</dd></div>
        ))}
      </dl>
      {sc.ctx.has("staff.bank_view_full") && b ? <p className={c.note}>Showing the full number is recorded in the audit log.</p> : null}
    </div>
  );
}

interface Form {
  pan: string; aadhaar: string; dob: string; address: string;
  eName: string; ePhone: string; eRelation: string;
  holder: string; account: string; ifsc: string; bankName: string;
}

const formOf = (x: StaffDetails | null): Form => ({
  pan: x?.pan ?? "", aadhaar: "", dob: x?.dob ?? "", address: x?.address ?? "",
  eName: x?.emergencyContact?.name ?? "", ePhone: "", eRelation: x?.emergencyContact?.relation ?? "",
  holder: x?.bank?.holderName ?? "", account: "", ifsc: x?.bank?.ifsc ?? "", bankName: x?.bank?.bankName ?? "",
});

/** Client-side checks mirroring the server; returns field → message. */
export function validateForm(f: Form, current: StaffDetails | null): Partial<Record<keyof Form, string>> {
  const out: Partial<Record<keyof Form, string>> = {};
  if (f.pan.trim() && !PAN_RE.test(digitsOf(f.pan).toUpperCase())) out.pan = "5 letters, 4 digits, 1 letter (like ABCDE1234F)";
  if (f.aadhaar.trim() && !/^\d{12}$/.test(digitsOf(f.aadhaar))) out.aadhaar = "Enter all 12 digits";
  const anyEmergency = f.eName.trim() || f.ePhone.trim() || f.eRelation.trim();
  if (anyEmergency) {
    if (!f.eName.trim()) out.eName = "Name is needed";
    if (!f.eRelation.trim()) out.eRelation = "Relation is needed";
    if (!f.ePhone.trim() && !current?.emergencyContact) out.ePhone = "Phone is needed";
  }
  const anyBank = f.holder.trim() || f.account.trim() || f.ifsc.trim() || f.bankName.trim();
  if (anyBank) {
    if (!f.holder.trim()) out.holder = "Account holder is needed";
    if (!IFSC_RE.test(f.ifsc.trim().toUpperCase())) out.ifsc = "11 characters, the 5th is 0 (like SBIN0001234)";
    if (f.account.trim() && !/^\d{9,18}$/.test(digitsOf(f.account))) out.account = "9 to 18 digits";
    if (!f.account.trim() && !current?.bank?.accountLast4) out.account = "Account number is needed";
  }
  return out;
}

/** Only what changed goes to the server. Blank Aadhaar / account / phone means "keep what is stored". */
export function patchOf(f: Form, current: StaffDetails | null): DetailsPatch {
  const p: DetailsPatch = {};
  const t = (s: string) => s.trim();
  const pan = t(f.pan) ? digitsOf(f.pan).toUpperCase() : null;
  if (pan !== (current?.pan ?? null)) p.pan = pan;
  if (t(f.aadhaar)) p.aadhaar = digitsOf(f.aadhaar);
  if ((t(f.dob) || null) !== (current?.dob ?? null)) p.dob = t(f.dob) || null;
  if ((t(f.address) || null) !== (current?.address ?? null)) p.address = t(f.address) || null;
  const anyEmergency = t(f.eName) || t(f.ePhone) || t(f.eRelation);
  if (!anyEmergency && current?.emergencyContact) p.emergencyContact = null;
  else if (anyEmergency) {
    const cur = current?.emergencyContact;
    if (t(f.ePhone) || t(f.eName) !== cur?.name || t(f.eRelation) !== (cur?.relation ?? "")) {
      // The phone shown is masked; re-sending needs the real one, so a name/relation change without a phone is refused client-side.
      if (t(f.ePhone)) p.emergencyContact = { name: t(f.eName), phone: t(f.ePhone), relation: t(f.eRelation) };
    }
  }
  const anyBank = t(f.holder) || t(f.account) || t(f.ifsc) || t(f.bankName);
  if (!anyBank && current?.bank) p.bank = null;
  else if (anyBank) {
    const cur = current?.bank;
    const ifsc = t(f.ifsc).toUpperCase();
    const bankName = t(f.bankName) || null;
    if (t(f.account) || t(f.holder) !== cur?.holderName || ifsc !== cur?.ifsc || bankName !== (cur?.bankName ?? null)) {
      p.bank = { holderName: t(f.holder), ifsc, bankName, ...(t(f.account) ? { accountNumber: digitsOf(f.account) } : {}) };
    }
  }
  if (Object.keys(p).length > 0 && current) p.version = current.version;
  return p;
}

function DetailsDrawer({ open, onClose, staff, details }: { open: boolean; onClose: () => void; staff: StaffMember; details: StaffDetails | null }) {
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState<Form>(() => formOf(details));
  const [errors, setErrors] = useState<Partial<Record<keyof Form, string>>>({});
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setF(formOf(details)); setErrors({}); setError(null); } }, [open, details]);
  const save = useMutation({
    mutationFn: (p: DetailsPatch) => saveDetails(client, staff.id, p),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["staff"] });
      toast.show(r.changed.length ? "Details saved" : "Nothing changed");
      onClose();
    },
    onError: (e) => setError(messageOf(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const errs = validateForm(f, details);
    // Changing the emergency name or relation needs the phone again (the stored one is masked here).
    if (!errs.ePhone && (f.eName.trim() || f.eRelation.trim()) && !f.ePhone.trim() && details?.emergencyContact && (f.eName.trim() !== details.emergencyContact.name || f.eRelation.trim() !== (details.emergencyContact.relation ?? ""))) errs.ePhone = "Re-enter the phone to change this contact";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    save.mutate(patchOf(f, details));
  };
  const input = (k: keyof Form, label: string, opts: { hint?: string; type?: string; placeholder?: string; inputMode?: "numeric" | "text" | "tel"; autoComplete?: string; maxLength?: number } = {}) => (
    <Field label={label} htmlFor={`sd-${k}`} hint={errors[k] ?? opts.hint}>
      <input
        id={`sd-${k}`}
        className={c.input}
        type={opts.type ?? "text"}
        value={f[k]}
        placeholder={opts.placeholder}
        inputMode={opts.inputMode}
        autoComplete={opts.autoComplete ?? "off"}
        maxLength={opts.maxLength}
        aria-invalid={errors[k] ? true : undefined}
        onChange={(e) => setF({ ...f, [k]: e.target.value })}
      />
    </Field>
  );
  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()} title={`${staff.name}: ID and bank details`} description="Only the fields you change are saved.">
      <form className={`${c.stack} ${c.drawerPad}`} onSubmit={submit} noValidate aria-label="Edit ID and bank details">
        <FormError message={error} />
        <h3 className={d.h3}>Identity</h3>
        {input("pan", "PAN", { placeholder: "ABCDE1234F", maxLength: 12 })}
        {input("aadhaar", "Aadhaar", { inputMode: "numeric", maxLength: 14, placeholder: details?.aadhaarLast4 ? `Stored: XXXX XXXX ${details.aadhaarLast4}` : "12 digits", hint: "Enter all 12 digits. Only the last 4 are kept; leave blank to keep what is stored." })}
        {input("dob", "Date of birth", { type: "date" })}
        <Field label="Address" htmlFor="sd-address" hint={errors.address}>
          <textarea id="sd-address" className={`${c.input} ${d.area}`} value={f.address} maxLength={500} onChange={(e) => setF({ ...f, address: e.target.value })} />
        </Field>
        <h3 className={d.h3}>Emergency contact</h3>
        {input("eName", "Name", { maxLength: 120 })}
        {input("ePhone", "Phone", { type: "tel", inputMode: "tel", placeholder: details?.emergencyContact?.phone ? `Stored: ${details.emergencyContact.phone}` : undefined, hint: details?.emergencyContact ? "Leave blank to keep the stored number." : undefined })}
        {input("eRelation", "Relation", { maxLength: 60, placeholder: "Mother, spouse, friend…" })}
        <h3 className={d.h3}>Bank</h3>
        {input("holder", "Account holder name", { maxLength: 120 })}
        {input("account", "Account number", { inputMode: "numeric", maxLength: 22, placeholder: details?.bank?.accountMasked ? `Stored: ${details.bank.accountMasked}` : "9 to 18 digits", hint: details?.bank ? "Leave blank to keep the stored number. Stored encrypted." : "Stored encrypted; shown masked." })}
        {input("ifsc", "IFSC", { placeholder: "SBIN0001234", maxLength: 11 })}
        {input("bankName", "Bank name (optional)", { maxLength: 120 })}
        <div className={c.row}>
          <Button variant="primary" type="submit" busy={save.isPending}>Save details</Button>
          <Button variant="plain" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Drawer>
  );
}

type RowStatus = { state: "uploading"; name: string } | { state: "failed"; message: string };

/** Fetches a signed, single-use link and saves the file without putting the link in history or a new tab. */
async function downloadVia(url: string, filename: string) {
  const res = await fetch(url, { referrerPolicy: "no-referrer", cache: "no-store" });
  if (!res.ok) throw new Error("The download link expired. Try again.");
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

function DocumentsCard({ staff }: { staff: StaffMember }) {
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<Partial<Record<DocumentKind, RowStatus>>>({});
  const [error, setError] = useState<string | null>(null);
  const key = ["staff", ...sc.key, "documents", staff.id];
  const q = useQuery({ queryKey: key, queryFn: () => listDocuments(client, staff.id), retry: false });
  const canEdit = sc.ctx.has("staff.documents_edit");
  const canDownload = sc.ctx.has("staff.documents_download");
  const refresh = () => void qc.invalidateQueries({ queryKey: key });

  const upload = async (kind: DocumentKind, file: File) => {
    const problem = fileProblem(file);
    if (problem) { setStatus((s) => ({ ...s, [kind]: { state: "failed", message: problem } })); return; }
    setStatus((s) => ({ ...s, [kind]: { state: "uploading", name: file.name } }));
    try {
      await uploadDocument(client, staff.id, kind, file, file.name);
      setStatus((s) => { const n = { ...s }; delete n[kind]; return n; });
      toast.show(`${DOCUMENT_LABEL[kind]} uploaded`);
      refresh();
    } catch (e) {
      setStatus((s) => ({ ...s, [kind]: { state: "failed", message: messageOf(e) } }));
    }
  };
  const remove = useMutation({ mutationFn: (id: string) => deleteDocument(client, staff.id, id), onSuccess: () => { toast.show("Document deleted"); refresh(); }, onError: (e) => setError(messageOf(e)) });
  const download = useMutation({
    mutationFn: async (doc: StaffDocument) => downloadVia((await documentLink(client, staff.id, doc.id)).url, doc.filename),
    onSuccess: () => setError(null),
    onError: (e) => setError(messageOf(e)),
  });

  return (
    <Card aria-label="Documents">
      <CardHeader title="Documents" subtitle="PDF, JPEG, PNG or WebP, up to 5 MB each. Private: never a public link." />
      {q.isPending ? (
        <RowsSkeleton rows={5} />
      ) : q.error ? (
        <div className={c.pad}><Failure error={q.error} title="Could not load documents" onRetry={() => void q.refetch()} /></div>
      ) : (
        <>
          <FormError message={error} />
          {q.data.documents.length === 0 ? <p className={`${c.note} ${d.emptyNote}`}>No documents uploaded yet.{canEdit ? " Upload each one from its row." : ""}</p> : null}
          <ul className={d.rows} aria-label="Document rows">
            {DOCUMENT_KINDS.map((kind) => (
              <DocRow
                key={kind}
                kind={kind}
                docs={q.data.documents.filter((x) => x.kind === kind)}
                status={status[kind]}
                canEdit={canEdit}
                canDownload={canDownload}
                locale={sc.ctx.locale}
                timeZone={sc.ctx.timeZone}
                onPick={(file) => void upload(kind, file)}
                onDelete={(id) => remove.mutate(id)}
                deleting={remove.isPending ? remove.variables : undefined}
                onDownload={(doc) => download.mutate(doc)}
                downloading={download.isPending ? download.variables?.id : undefined}
              />
            ))}
          </ul>
          {canDownload ? <p className={`${c.note} ${d.emptyNote}`}>Every download is recorded in the audit log.</p> : null}
        </>
      )}
    </Card>
  );
}

function DocRow({ kind, docs, status, canEdit, canDownload, locale, timeZone, onPick, onDelete, deleting, onDownload, downloading }: {
  kind: DocumentKind; docs: StaffDocument[]; status: RowStatus | undefined; canEdit: boolean; canDownload: boolean; locale: string; timeZone: string;
  onPick: (f: File) => void; onDelete: (id: string) => void; deleting: string | undefined; onDownload: (d: StaffDocument) => void; downloading: string | undefined;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const label = DOCUMENT_LABEL[kind];
  const badge = status?.state === "uploading" ? <Badge tone="info">Uploading…</Badge> : status?.state === "failed" ? <Badge tone="crit">Failed</Badge> : docs.length ? <Badge tone="ok">Uploaded</Badge> : <Badge>Not uploaded</Badge>;
  return (
    <li className={d.row} aria-label={label}>
      <div className={d.rowHead}>
        <div className={c.row}><strong>{label}</strong>{badge}</div>
        {canEdit ? (
          <>
            <input ref={fileRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp" aria-label={`Choose ${label} file`} onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; if (file) onPick(file); }} />
            <Button variant="plain" disabled={status?.state === "uploading"} onClick={() => fileRef.current?.click()}>{docs.length ? "Upload another" : "Upload"}</Button>
          </>
        ) : null}
      </div>
      {status?.state === "failed" ? <p className={d.fail} role="alert">{status.message}</p> : null}
      {status?.state === "uploading" ? <p className={c.note}>{status.name}</p> : null}
      {docs.map((doc) => (
        <div key={doc.id} className={d.file}>
          <span className={c.trunc}><span className={d.mono}>{doc.filename}</span> · {formatBytes(doc.bytes)} · {formatDate(localDate(doc.createdAt, timeZone), locale, "short")}</span>
          <span className={c.row}>
            {canDownload ? <Button variant="plain" busy={downloading === doc.id} onClick={() => onDownload(doc)}>Download</Button> : null}
            {canEdit ? <Confirm label="Delete" question={`Delete ${doc.filename}?`} busy={deleting === doc.id} onConfirm={() => onDelete(doc.id)} /> : null}
          </span>
        </div>
      ))}
    </li>
  );
}
